import { io, Socket } from 'socket.io-client';
import config from '../config';
import type {
  WebSocketMessage,
  WebSocketTyping,
  WebSocketError,
  WebSocketRoomFull,
  TicketMessage,
  Ticket,
} from '@/types/ticket';
import type { LeadQueryWebSocketMessage } from '@/types/lead-query';
import { getAccessTokenFromCookies } from '@/lib/jwt-utils';
import type { IncomingLeadQueryOffer } from '@/types/lead-query';

/**
 * Single Socket.IO connection for admin: support tickets + lead queries inbox.
 */
type IncomingLeadQueryHandler = (
  payload: IncomingLeadQueryOffer,
  ack: (res: { accepted: boolean }) => void
) => void;

class AdminSocketService {
  private socket: Socket | null = null;
  private connectInFlight: Promise<void> | null = null;
  /** Rejects the in-flight `connect()` promise when `disconnect()` runs during handshake. */
  private pendingConnectReject: ((err: Error) => void) | null = null;
  private readonly incomingLeadQueryHandlerWrappers = new Map<IncomingLeadQueryHandler, IncomingLeadQueryHandler>();

  /**
   * Connect to the API Socket.IO server using the same origin as REST.
   * Pass an explicit JWT when you have it in memory; otherwise the readable `adminAccessToken` cookie is used, and HttpOnly session cookies are sent via `withCredentials`.
   */
  connect(explicitAccessToken?: string): Promise<void> {
    if (this.socket?.connected) {
      return Promise.resolve();
    }
    if (this.connectInFlight) {
      return this.connectInFlight;
    }

    this.connectInFlight = new Promise((resolve, reject) => {
      this.pendingConnectReject = reject;

      const options: Record<string, unknown> = {
        path: config.socketPath,
        transports: ['websocket', 'polling'],
        withCredentials: true,
        reconnection: true,
        reconnectionAttempts: Infinity,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
      };

      const handshakeToken = explicitAccessToken || getAccessTokenFromCookies();
      if (handshakeToken) {
        options.auth = { token: handshakeToken, client: 'admin' };
      } else {
        options.auth = { client: 'admin' };
      }

      const CONNECT_TIMEOUT_MS = 45000;
      let settled = false;

      const finish = (fn: () => void) => {
        if (settled) return;
        settled = true;
        clearTimeout(timeoutTimer);
        this.pendingConnectReject = null;
        fn();
        this.connectInFlight = null;
      };

      const timeoutTimer = setTimeout(() => {
        finish(() =>
          reject(new Error('Admin socket: connection timed out (check API URL, CORS, and auth cookies)'))
        );
      }, CONNECT_TIMEOUT_MS);

      if (this.socket) {
        try {
          this.socket.removeAllListeners();
          this.socket.disconnect();
        } catch {
          /* ignore */
        }
        this.socket = null;
      }

      // `apiUrl` is the same base as REST — do not append `/socket.io`; `path` sets the engine path.
      this.socket = io(config.apiUrl, options as never);

      this.socket.once('connect', () => {
        console.log('🔗 Connected to admin WebSocket');
        finish(() => resolve());
      });

      this.socket.on('disconnect', (reason: string) => {
        console.log('🔌 Disconnected from admin WebSocket:', reason);
      });

      this.socket.on('connect_error', (error: Error) => {
        console.error('❌ Admin socket connect_error:', error.message);
        if (!this.socket?.active) {
          finish(() => reject(error));
        }
      });

      this.socket.on('error', (error: WebSocketError) => {
        console.error('❌ WebSocket error:', error);
      });

      this.socket.io.on('reconnect', (attempt: number) => {
        console.log('🔄 Admin socket reconnected, attempt:', attempt);
      });

      this.socket.io.on('reconnect_failed', () => {
        console.error('❌ Admin socket: reconnect_failed');
      });
    });

    return this.connectInFlight;
  }

  /**
   * Permanently tear down the client (e.g. logout). Rejects any in-progress `connect()` attempt.
   * Registered event listeners are cleared with the socket instance.
   */
  disconnect() {
    if (this.pendingConnectReject) {
      this.pendingConnectReject(new Error('Admin socket: disconnected'));
      this.pendingConnectReject = null;
    }
    this.connectInFlight = null;
    if (this.socket) {
      try {
        this.socket.disconnect();
      } catch {
        /* ignore */
      }
      this.socket = null;
    }
  }

  /**
   * After REST refresh rotates cookies/JWT, perform a new handshake while keeping the same client
   * and listener registrations (Socket.IO reconnect with fresh cookies / `auth.token`).
   */
  async reconnectAfterAuthRefresh(): Promise<void> {
    if (!this.socket) {
      try {
        await this.connect();
      } catch {
        /* no-op */
      }
      return;
    }

    const s = this.socket;
    const fresh = getAccessTokenFromCookies();
    if (fresh) {
      s.auth = { ...(s.auth as Record<string, unknown> | undefined), token: fresh };
    }

    try {
      await new Promise<void>((resolve, reject) => {
        const CONNECT_TIMEOUT_MS = 45000;
        const timer = window.setTimeout(() => {
          s.off('connect', onConnect);
          s.off('connect_error', onErr);
          reject(new Error('Admin socket: reconnect after auth timed out'));
        }, CONNECT_TIMEOUT_MS);

        const onConnect = () => {
          window.clearTimeout(timer);
          s.off('connect', onConnect);
          s.off('connect_error', onErr);
          resolve();
        };

        const onErr = (err: Error) => {
          window.clearTimeout(timer);
          s.off('connect', onConnect);
          s.off('connect_error', onErr);
          reject(err);
        };

        s.once('connect', onConnect);
        s.once('connect_error', onErr);
        s.disconnect();
        s.connect();
      });
    } catch {
      /* UI still shows disconnected */
    }
  }

  // --- Support tickets ---

  joinTicket(ticketId: string) {
    if (!this.socket?.connected) {
      console.warn('Socket not connected, cannot join ticket');
      return;
    }
    this.socket.emit('join-ticket', { ticketId });
  }

  leaveTicket(ticketId: string) {
    if (!this.socket?.connected) return;
    this.socket.emit('leave-ticket', { ticketId });
  }

  sendMessage(ticketId: string, message: string) {
    if (!this.socket?.connected) {
      console.warn('Socket not connected, cannot send message');
      return;
    }
    this.socket.emit('send-message', { ticketId, message });
  }

  startTyping(ticketId: string) {
    if (!this.socket?.connected) return;
    this.socket.emit('typing-start', { ticketId });
  }

  stopTyping(ticketId: string) {
    if (!this.socket?.connected) return;
    this.socket.emit('typing-stop', { ticketId });
  }

  markMessageRead(messageId: string) {
    if (!this.socket?.connected) return;
    this.socket.emit('mark-message-read', { messageId });
  }

  onNewMessage(callback: (data: WebSocketMessage) => void) {
    this.socket?.on('new-message', callback);
  }

  onTicketUpdated(
    callback: (data: {
      ticketId: string;
      message?: WebSocketMessage;
      ticket?: Partial<Ticket>;
    }) => void
  ) {
    this.socket?.on('ticket-updated', callback);
  }

  onMessageEdited(callback: (data: { ticketId: string; message: TicketMessage }) => void) {
    this.socket?.on('message-edited', callback);
  }

  onMessageDeleted(callback: (data: { ticketId: string; messageId: string }) => void) {
    this.socket?.on('message-deleted', callback);
  }

  onMessageUnsent(
    callback: (data: {
      ticketId: string;
      messageId: string;
      unsentAt?: string;
      isUnsent?: boolean;
    }) => void
  ) {
    this.socket?.on('message-unsent', callback);
  }

  onMessageSent(callback: (data: { messageId: string; ticketId: string }) => void) {
    this.socket?.on('message-sent', callback);
  }

  onMessageError(callback: (data: { messageId: string; error: unknown }) => void) {
    this.socket?.on('message-error', callback);
  }

  onJoinedTicket(callback: (data: { ticketId: string }) => void) {
    this.socket?.on('joined-ticket', callback);
  }

  onUserTyping(callback: (data: WebSocketTyping) => void) {
    this.socket?.on('user-typing', callback);
  }

  onUserStoppedTyping(callback: (data: { ticketId: string; senderType: string }) => void) {
    this.socket?.on('user-stopped-typing', callback);
  }

  onRoomFull(callback: (data: WebSocketRoomFull) => void) {
    this.socket?.on('room-full', callback);
  }

  onError(callback: (error: WebSocketError) => void) {
    this.socket?.on('error', callback);
  }

  onDisconnect(callback: () => void) {
    this.socket?.on('disconnect', callback);
  }

  /** Fires on every successful connection, including after automatic reconnect (use to clear "offline" UI). */
  onSocketConnect(callback: () => void) {
    this.socket?.on('connect', callback);
  }

  /** Manager emits `reconnect` after a successful reconnection (socket.io v4). */
  onReconnect(callback: (attempt: number) => void) {
    this.socket?.io.on('reconnect', callback);
  }

  offNewMessage(callback: (data: WebSocketMessage) => void) {
    this.socket?.off('new-message', callback);
  }

  offTicketUpdated(
    callback: (data: {
      ticketId: string;
      message?: WebSocketMessage;
      ticket?: Partial<Ticket>;
    }) => void
  ) {
    this.socket?.off('ticket-updated', callback);
  }

  offMessageEdited(callback: (data: { ticketId: string; message: TicketMessage }) => void) {
    this.socket?.off('message-edited', callback);
  }

  offMessageDeleted(callback: (data: { ticketId: string; messageId: string }) => void) {
    this.socket?.off('message-deleted', callback);
  }

  offMessageUnsent(callback: (data: { ticketId: string; messageId: string }) => void) {
    this.socket?.off('message-unsent', callback);
  }

  offMessageSent(callback: (data: { messageId: string; ticketId: string }) => void) {
    this.socket?.off('message-sent', callback);
  }

  offMessageError(callback: (data: { messageId: string; error: unknown }) => void) {
    this.socket?.off('message-error', callback);
  }

  offJoinedTicket(callback: (data: { ticketId: string }) => void) {
    this.socket?.off('joined-ticket', callback);
  }

  offUserTyping(callback: (data: WebSocketTyping) => void) {
    this.socket?.off('user-typing', callback);
  }

  offUserStoppedTyping(callback: (data: { ticketId: string; senderType: string }) => void) {
    this.socket?.off('user-stopped-typing', callback);
  }

  offRoomFull(callback: (data: WebSocketRoomFull) => void) {
    this.socket?.off('room-full', callback);
  }

  offError(callback: (error: WebSocketError) => void) {
    this.socket?.off('error', callback);
  }

  offDisconnect(callback: () => void) {
    this.socket?.off('disconnect', callback);
  }

  offSocketConnect(callback: () => void) {
    this.socket?.off('connect', callback);
  }

  offReconnect(callback: (attempt: number) => void) {
    this.socket?.io.off('reconnect', callback);
  }

  // --- Lead queries (website leads inbox) ---

  setAvailability(
    status: 'available' | 'offline',
    ack?: (res: { ok: true } | { ok: false; code: string; message?: string }) => void
  ) {
    if (!this.socket?.connected) {
      ack?.({ ok: false, code: 'SOCKET_NOT_CONNECTED' });
      return;
    }
    this.socket.emit('admin-availability:set', { status }, ack);
  }

  joinLeadQuery(queryId: string) {
    if (!this.socket?.connected) {
      console.warn('Socket not connected, cannot join lead query');
      return;
    }
    this.socket.emit('join-lead-query', { queryId });
  }

  leaveLeadQuery(queryId: string) {
    if (!this.socket?.connected) return;
    this.socket.emit('leave-lead-query', { queryId });
  }

  sendLeadQueryMessage(queryId: string, message: string) {
    if (!this.socket?.connected) {
      console.warn('Socket not connected, cannot send lead query message');
      return;
    }
    this.socket.emit('send-lead-query-message', { queryId, message });
  }

  /** Mark a lead-originated message as read (server event `mark-lead-query-message-read`). */
  markLeadQueryMessageRead(messageId: string) {
    if (!this.socket?.connected || !messageId) return;
    this.socket.emit('mark-lead-query-message-read', { messageId });
  }

  onLeadQueryNewMessage(callback: (data: LeadQueryWebSocketMessage) => void) {
    this.socket?.on('new-lead-query-message', callback);
  }

  offLeadQueryNewMessage(callback: (data: LeadQueryWebSocketMessage) => void) {
    this.socket?.off('new-lead-query-message', callback);
  }

  onLeadQueryMessageSent(callback: (data: { messageId: string; queryId: string }) => void) {
    this.socket?.on('lead-query-message-sent', callback);
  }

  offLeadQueryMessageSent(callback: (data: { messageId: string; queryId: string }) => void) {
    this.socket?.off('lead-query-message-sent', callback);
  }

  onJoinedLeadQuery(callback: (data: { queryId: string }) => void) {
    this.socket?.on('joined-lead-query', callback);
  }

  offJoinedLeadQuery(callback: (data: { queryId: string }) => void) {
    this.socket?.off('joined-lead-query', callback);
  }

  onIncomingLeadQuery(callback: IncomingLeadQueryHandler) {
    const wrapped: IncomingLeadQueryHandler = (payload, ack) => callback(payload, ack);
    this.incomingLeadQueryHandlerWrappers.set(callback, wrapped);
    this.socket?.on('incoming-lead-query', wrapped);
  }

  offIncomingLeadQuery(callback: IncomingLeadQueryHandler) {
    const wrapped = this.incomingLeadQueryHandlerWrappers.get(callback);
    if (wrapped) {
      this.socket?.off('incoming-lead-query', wrapped);
      this.incomingLeadQueryHandlerWrappers.delete(callback);
    }
  }

  onLeadQueryHandoffSuccess(callback: (data: { queryId: string }) => void) {
    this.socket?.on('lead-query-handoff-success', callback);
  }

  offLeadQueryHandoffSuccess(callback: (data: { queryId: string }) => void) {
    this.socket?.off('lead-query-handoff-success', callback);
  }

  onTicketCreated(callback: (data: { ticketId: string }) => void) {
    this.socket?.on('ticket-created', callback);
  }

  offTicketCreated(callback: (data: { ticketId: string }) => void) {
    this.socket?.off('ticket-created', callback);
  }

  onAdminNotificationsRefresh(
    callback: (data: {
      reason: string;
      category?: string;
      priority?: string;
      at?: string;
    }) => void
  ) {
    this.socket?.on('admin-notifications:refresh', callback);
  }

  offAdminNotificationsRefresh(
    callback: (data: {
      reason: string;
      category?: string;
      priority?: string;
      at?: string;
    }) => void
  ) {
    this.socket?.off('admin-notifications:refresh', callback);
  }

  get connected() {
    return Boolean(this.socket?.connected);
  }
}

export const adminSocket = new AdminSocketService();

/** @deprecated Use adminSocket; kept for existing ticket screens. */
export const ticketWebSocket = adminSocket;
